import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useContent } from '../hooks/useContent';
import { useSite } from '../hooks/useSites';
import { useToast } from '../hooks/useToast';
import { useUnsavedChanges } from '../hooks/useUnsavedChanges';
import { useTemplateManifest } from '../hooks/useTemplateManifest';
import { useEditorPreview } from '../hooks/useEditorPreview';
import { getSchema } from '../api/schemas';
import { getContentId } from '../types';
import FormField from '../components/FormField';
import '../styles/editor.css';

// Fields handled by other dedicated editors — exclude from this page
const EXCLUDED_FIELDS = new Set([
  'logo', 'favicon', 'tagline', 'sectionLabel', // BrandingEditor
  'ctaTitle', 'ctaSubtitle', 'ctaButtonText', 'ctaButtonLink', // CtaEditor
  'metaTitle', 'metaDescription', 'ogImage', 'googleVerification', // SeoPage
  'overridePrimaryColor', 'overrideSecondaryColor', 'overrideBackgroundColor',
  'overrideTextColor', 'overrideHeadingColor', 'overrideHeadingFont', 'overrideBodyFont', // legacy v1 overrides
]);

const SECTION_GROUPS: { label: string; fields: string[] }[] = [
  { label: 'Products', fields: ['showProducts', 'productsTitle', 'productsSubtitle', 'productsButtonText', 'featuredTitle', 'featuredSubtitle'] },
  { label: 'Gallery', fields: ['showGallery'] },
  { label: 'About', fields: ['showAbout', 'aboutButtonText'] },
  { label: 'Contact', fields: ['showContact', 'contactTitle'] },
  { label: 'Blog', fields: ['showBlog', 'blogTitle', 'blogSubtitle', 'blogButtonText'] },
  { label: 'Testimonials', fields: ['showTestimonials', 'testimonialsTitle', 'testimonialsSubtitle'] },
  { label: 'Opening Hours', fields: ['showHours', 'hoursTitle'] },
  { label: 'Menu', fields: ['showMenu', 'menuTitle', 'menuSubtitle'] },
  { label: 'Social & Reviews', fields: ['googleReviewsUrl', 'instagramUrl'] },
];

const ALL_GROUPED_FIELDS = new Set(SECTION_GROUPS.flatMap((g) => g.fields));

export default function SectionsEditor() {
  const { siteId } = useParams<{ siteId: string }>();
  const { site } = useSite(siteId);
  const { items, loading, create, update } = useContent(siteId, 'site');
  const { addToast } = useToast();
  const { filterFields } = useTemplateManifest(site?.templateId);
  const { sendUpdate } = useEditorPreview('/');

  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [originalData, setOriginalData] = useState<Record<string, unknown>>({});
  const [saving, setSaving] = useState(false);

  const schema = site ? getSchema(site.templateId) : undefined;
  const rawContentType = schema?.contentTypes.site;
  const filteredContentType = rawContentType ? filterFields('site', rawContentType) : undefined;

  // Manifest-allowed fields, minus those handled by other editors
  const allFields = Object.fromEntries(
    Object.entries(filteredContentType?.fields || {}).filter(([name]) => !EXCLUDED_FIELDS.has(name))
  );

  const existingItem = items[0];
  const isDirty = JSON.stringify(formData) !== JSON.stringify(originalData);
  useUnsavedChanges(isDirty);

  useEffect(() => {
    if (existingItem) {
      const data = existingItem.data || {};
      setFormData(data);
      setOriginalData(data);
    }
  }, [existingItem]);

  const handleChange = (name: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    sendUpdate('site', name, value);
  };

  const handleSave = async () => {
    if (!siteId) return;
    setSaving(true);
    try {
      if (existingItem) {
        await update(getContentId(existingItem.contentKey), formData);
      } else {
        await create(formData);
      }
      setOriginalData({ ...formData });
      addToast('success', 'Section settings saved');
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="page">
        <div className="loading-state"><div className="spinner" /></div>
      </div>
    );
  }

  const hasFields = Object.keys(allFields).length > 0;

  // Fields not matched by any group (catch-all for future fields)
  const ungroupedFields = Object.fromEntries(
    Object.entries(allFields).filter(([name]) => !ALL_GROUPED_FIELDS.has(name))
  );

  return (
    <div className="page">
      <div className="page-header">
        <h1>Sections</h1>
        <div className="page-header-actions">
          {isDirty && <span className="unsaved-indicator">Unsaved changes</span>}
          <button className="btn btn-primary" onClick={handleSave} disabled={saving || !hasFields}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>

      {!hasFields ? (
        <div className="empty-state">
          <p>Your template doesn't have customisable section settings.</p>
        </div>
      ) : (
        <div className="editor-form">
          {SECTION_GROUPS.map(({ label, fields: groupFieldNames }) => {
            const groupFields = Object.fromEntries(
              groupFieldNames
                .filter((name) => name in allFields)
                .map((name) => [name, allFields[name]])
            );
            if (Object.keys(groupFields).length === 0) return null;
            return (
              <div key={label} className="editor-section-group">
                <h2 className="editor-section-group-title">{label}</h2>
                {Object.entries(groupFields).map(([name, fieldSchema]) => (
                  <FormField
                    key={name}
                    name={name}
                    schema={fieldSchema}
                    value={formData[name]}
                    onChange={handleChange}
                    siteId={siteId!}
                    cmsType="site"
                  />
                ))}
              </div>
            );
          })}
          {Object.entries(ungroupedFields).map(([name, fieldSchema]) => (
            <FormField
              key={name}
              name={name}
              schema={fieldSchema}
              value={formData[name]}
              onChange={handleChange}
              siteId={siteId!}
              cmsType="site"
            />
          ))}
        </div>
      )}
    </div>
  );
}
